import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CheckCircle, CloseCircle, InfoCircle } from "@solar-icons/react"
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import type { CookiePreferences } from "@/utils/cookies";

interface CookiePreferencesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preferences: CookiePreferences;
  onSave: (preferences: CookiePreferences) => void;
}

export function CookiePreferencesSheet({
  open,
  onOpenChange,
  preferences,
  onSave,
}: CookiePreferencesSheetProps) {
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>(preferences);

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === "essential") return; // Essential cookies cannot be disabled
    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    onSave(localPreferences);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    onSave(allAccepted);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto max-h-[90vh] overflow-y-auto rounded-none">
        <SheetHeader className="pb-3">
          <SheetTitle className="text-lg">Cookie Preferences</SheetTitle>
          <SheetDescription className="text-sm">
            Manage your cookie preferences. You can enable or disable different types of cookies
            below.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-3 py-2">
          {/* Essential Cookies */}
          <div className="space-y-2 p-3 rounded-lg border bg-muted/50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="essential" className="text-sm font-semibold">
                  Essential Cookies
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These cookies are necessary for the website to function and cannot be switched off
                  in our systems.
                </p>
              </div>
              <Switch
                id="essential"
                checked={localPreferences.essential}
                disabled
                className="ml-3"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-background p-2 rounded">
              <InfoCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>
                Essential cookies are always active. They are usually only set in response to
                actions made by you such as setting your privacy preferences, logging in, or filling
                in forms.
              </p>
            </div>
          </div>

          <Separator />

          {/* Analytics Cookies */}
          <div className="space-y-2 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-sm font-semibold">
                  Analytics Cookies
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These cookies help us understand how visitors interact with our website by
                  collecting and reporting information anonymously.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={localPreferences.analytics}
                onCheckedChange={() => handleToggle("analytics")}
                className="ml-3"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              <InfoCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>
                Analytics cookies allow us to count visits and traffic sources so we can measure
                and improve the performance of our site.
              </p>
            </div>
          </div>

          <Separator />

          {/* Marketing Cookies */}
          <div className="space-y-2 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="marketing" className="text-sm font-semibold">
                  Marketing Cookies
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These cookies may be set through our site by our advertising partners to build a
                  profile of your interests.
                </p>
              </div>
              <Switch
                id="marketing"
                checked={localPreferences.marketing}
                onCheckedChange={() => handleToggle("marketing")}
                className="ml-3"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              <InfoCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>
                Marketing cookies are used to track visitors across websites to display relevant
                advertisements.
              </p>
            </div>
          </div>

          <Separator />

          {/* Functional Cookies */}
          <div className="space-y-2 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="functional" className="text-sm font-semibold">
                  Functional Cookies
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These cookies enable enhanced functionality and personalization, such as videos
                  and live chats.
                </p>
              </div>
              <Switch
                id="functional"
                checked={localPreferences.functional}
                onCheckedChange={() => handleToggle("functional")}
                className="ml-3"
              />
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              <InfoCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <p>
                Functional cookies allow the website to remember choices you make and provide
                enhanced, more personalized features.
              </p>
            </div>
          </div>
        </div>

        <SheetFooter className="flex-col sm:flex-row gap-2 sm:gap-0 pt-3 pb-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            size="sm"
            className="w-full sm:w-auto gap-1.5 text-xs sm:text-sm"
          >
            <CloseCircle className="h-3.5 w-3.5" />
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleAcceptAll}
            size="sm"
            className="w-full sm:w-auto gap-1.5 text-xs sm:text-sm"
          >
            <CheckCircle className="h-3.5 w-3.5" />
            Accept All
          </Button>
          <Button
            onClick={handleSave}
            size="sm"
            className="w-full sm:w-auto gap-1.5 text-xs sm:text-sm"
          >
            <CheckCircle className="h-3.5 w-3.5" />
            Save Preferences
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
